"use client"

import { useCallback, useEffect, useMemo, useRef } from "react"

export interface InViewTrackerOptions {
  rootMargin?: string
  /** Ratios at which the observer reports. Defaults to 0 (any sliver counts).
   *  Pass several when a consumer needs to judge HOW MUCH is on screen. */
  threshold?: number | number[]
  /**
   * Fired on EVERY intersecting report for an id — once per `threshold` crossed,
   * so several times as one card scrolls in — not once per enter. That is
   * deliberate: a consumer judging HOW MUCH is on screen needs the later
   * crossings, since the entry itself lands at a ratio of ~0. **A consumer with
   * a side effect must therefore dedupe** (see `useMarkWidgetsSeenOnView`).
   *
   * Carries the entry so consumers can apply their own rule ("half of it").
   * Held in a ref — an inline closure never rebuilds the observer.
   */
  onIntersect?: (id: string, entry: IntersectionObserverEntry) => void
}

/**
 * Track which row IDs are currently in the user's viewport, scoped to a
 * scroll container.
 *
 * Used by publishers (e.g. the recap publisher) to decide which rows to
 * include in the view-context snapshot — so the AI sees exactly what the
 * user can see, not the full filtered list.
 *
 * - `rootMargin` defaults to "200px 0px" so rows just above/below the
 *   fold (the "small margin" the AI is told about) are included.
 * - The set is mutated in place; consumers read it once at snapshot time
 *   via `readSet()` — no React state, no rerenders per scroll event.
 * - Order is insertion order of registration, but consumers should NOT
 *   rely on it — sort by their own list order when reading.
 */
export interface InViewTracker {
  /** Identity-stable — consumers thread it into ref callbacks, where a new
   *  identity costs an unobserve/re-observe of the element. */
  register: (id: string, el: HTMLElement | null) => void
  readSet: () => ReadonlySet<string>
}

export function useInViewTracker(
  scrollContainerRef: React.RefObject<HTMLElement | null>,
  options?: InViewTrackerOptions
): InViewTracker {
  const inView = useRef<Set<string>>(new Set())
  const observerRef = useRef<IntersectionObserver | null>(null)
  const elementToId = useRef<Map<Element, string>>(new Map())
  const pendingRegistrations = useRef<Map<string, HTMLElement>>(new Map())

  const rootMargin = options?.rootMargin ?? "200px 0px"
  // Round-tripped so the effect below can depend on it directly: an inline
  // `[0, 0.5]` is a new array every render, which would rebuild the observer on
  // each one. Keyed on the VALUE, this stays identity-stable.
  const thresholdKey = JSON.stringify(options?.threshold ?? 0)
  const threshold = useMemo(
    () => JSON.parse(thresholdKey) as number | number[],
    [thresholdKey]
  )
  const onIntersectRef = useRef(options?.onIntersect)
  onIntersectRef.current = options?.onIntersect

  useEffect(() => {
    const root = scrollContainerRef.current
    if (!root) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = elementToId.current.get(entry.target)
          if (!id) continue
          if (entry.isIntersecting) {
            inView.current.add(id)
            onIntersectRef.current?.(id, entry)
          } else {
            inView.current.delete(id)
          }
        }
      },
      { root, rootMargin, threshold }
    )
    observerRef.current = observer

    // Re-attach what is already bound. `register` is identity-stable, so a
    // consumer's ref callbacks fire once at mount and never again — nothing
    // would re-register these elements with a rebuilt observer (changed
    // options), and every one of them would go quiet for good.
    for (const element of elementToId.current.keys()) observer.observe(element)

    // Flush any registrations that arrived before the observer existed.
    for (const [id, el] of pendingRegistrations.current.entries()) {
      elementToId.current.set(el, id)
      observer.observe(el)
    }
    pendingRegistrations.current.clear()

    return () => {
      observer.disconnect()
      observerRef.current = null
      // `elementToId` deliberately survives — it is the binding the setup above
      // replays. Only the derived state goes.
      inView.current.clear()
    }
  }, [scrollContainerRef, rootMargin, threshold])

  // Every piece of state it touches is a ref, so it never needs to rebuild —
  // and it must not: consumers pass it into ref callbacks, where a fresh
  // identity makes React detach and re-attach every element on every render.
  const register = useCallback((id: string, el: HTMLElement | null): void => {
    const observer = observerRef.current

    if (!el) {
      // Caller is unmounting / detaching — find and unobserve.
      for (const [element, registeredId] of elementToId.current.entries()) {
        if (registeredId === id) {
          observer?.unobserve(element)
          elementToId.current.delete(element)
          inView.current.delete(id)
          break
        }
      }
      pendingRegistrations.current.delete(id)
      return
    }

    if (!observer) {
      pendingRegistrations.current.set(id, el)
      return
    }

    // If this id was previously bound to a different element, unobserve
    // the old one before binding the new (handles row reorders).
    for (const [element, registeredId] of elementToId.current.entries()) {
      if (registeredId === id && element !== el) {
        observer.unobserve(element)
        elementToId.current.delete(element)
        break
      }
    }
    elementToId.current.set(el, id)
    observer.observe(el)
  }, [])

  const readSet = (): ReadonlySet<string> => inView.current

  return { register, readSet }
}
